import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

// Add this interface declaration at the top of the file, after imports
declare global {
  interface Window {
    requestIdleCallback(callback: IdleRequestCallback, options?: IdleRequestOptions): number;
  }
}

interface IdleRequestOptions {
  timeout?: number;
}

type IdleRequestCallback = (deadline: IdleDeadline) => void;

interface IdleDeadline {
  readonly didTimeout: boolean;
  timeRemaining(): number;
}

@Injectable()
export class ChunkErrorInterceptor implements HttpInterceptor {
  private static readonly CHUNK_ERROR_PATTERNS = [
    /Loading chunk [\w-]+ failed/,
    /ChunkLoadError/,
    /Loading CSS chunk [\w-]+ failed/,
    /Loading module [\w-]+ failed/,
    /Failed to fetch dynamically imported module/,
    /Unable to preload CSS/,
  ] as const;

  private static readonly RELOAD_DELAY = 100;
  private static readonly MAX_RETRIES = 2;
  private static readonly RETRY_DELAY = 1000;

  // Track if we're already reloading to prevent multiple reloads
  private isReloading = false;

  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    // Only apply retry logic to chunk requests
    if (this.isChunkRequest(request)) {
      return next.handle(request).pipe(
        retry({
          count: ChunkErrorInterceptor.MAX_RETRIES,
          delay: (error, retryCount) => {
            if (this.isChunkError(error)) {
              console.warn(
                `Chunk loading error (attempt ${retryCount}/${ChunkErrorInterceptor.MAX_RETRIES}):`,
                error
              );
              return timer(ChunkErrorInterceptor.RETRY_DELAY * retryCount);
            }
            return throwError(() => error);
          },
        }),
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
    }

    return next
      .handle(request)
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (this.isChunkError(error) && !this.isReloading) {
      console.warn('Chunk loading error detected after retries, reloading application...', error);
      this.reloadApplication();
    }
    return throwError(() => error);
  }

  private isChunkRequest(request: HttpRequest<unknown>): boolean {
    const url = request.url;
    return (
      url.endsWith('.js') ||
      url.endsWith('.css') ||
      url.includes('/chunk-') ||
      url.includes('.module.') ||
      url.includes('.lazy.')
    );
  }

  private isChunkError(error: unknown): boolean {
    // Type guard for error
    if (!error || typeof error !== 'object') {
      return false;
    }

    const err = error as {
      message?: string;
      name?: string;
      status?: number;
      url?: string;
    };

    // Check error name
    if (err.name === 'ChunkLoadError') {
      return true;
    }

    // Check error message patterns
    if (err.message) {
      const message = err.message;
      return ChunkErrorInterceptor.CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
    }

    // Check for network errors on JS/CSS files
    if (err.status === 0 && err.url) {
      return /\.(js|css)(\?|$)/.test(err.url);
    }

    return false;
  }

  private reloadApplication(): void {
    if (this.isReloading) {
      return; // Prevent multiple reload attempts
    }

    this.isReloading = true;

    // Clear cache asynchronously
    this.clearCache().finally(() => {
      // Use requestIdleCallback for better performance if available
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(
          () => {
            window.location.reload();
          },
          { timeout: ChunkErrorInterceptor.RELOAD_DELAY }
        );
      } else {
        setTimeout(() => {
          window.location.reload();
        }, ChunkErrorInterceptor.RELOAD_DELAY);
      }
    });
  }

  private async clearCache(): Promise<void> {
    const clearPromises: Promise<void>[] = [];

    // Clear service worker caches
    if ('caches' in window) {
      clearPromises.push(
        caches
          .keys()
          .then((cacheNames) => {
            // Only clear caches related to your app
            const appCaches = cacheNames.filter(
              (name) => name.includes('angular') || name.includes('app-') || name.includes('chunk-')
            );

            return Promise.all(appCaches.map((cacheName) => caches.delete(cacheName)));
          })
          .then(() => undefined)
          .catch((err) => {
            console.warn('Failed to clear cache:', err);
          })
      );
    }

    // Unregister service workers if needed
    if ('serviceWorker' in navigator) {
      clearPromises.push(
        navigator.serviceWorker
          .getRegistrations()
          .then((registrations) => {
            return Promise.all(registrations.map((registration) => registration.unregister()));
          })
          .then(() => undefined)
          .catch((err) => {
            console.warn('Failed to unregister service workers:', err);
          })
      );
    }

    await Promise.allSettled(clearPromises);
  }
}
